// -----------------------------------------------------------------------------
// Rangos de fecha, en un solo lugar.
//
// Cada pantalla resolvía las fechas por su cuenta: el panel tenía atajos (Hoy,
// 7 días, Mes actual) y las tablas solo dos campos de fecha sueltos con un
// botón "Mes actual" pegado al lado. Quien quería "el mes pasado" tenía que
// calcular y escribir las dos fechas a mano.
//
// Todo se calcula en HORA LOCAL y nunca con toISOString(): ese método pasa a
// UTC, y en Chile un `new Date()` de la noche se convierte en el día
// siguiente. Un filtro contable corrido un día entrega otro total.
// -----------------------------------------------------------------------------

/** Fecha de hoy como YYYY-MM-DD, en la zona horaria del navegador. */
export function isoHoy(base = new Date()) {
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(
    base.getDate(),
  ).padStart(2, "0")}`;
}

/** Primer día del mes en curso. */
export function isoInicioMes(base = new Date()) {
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Corre una fecha ISO tantos días.
 *
 * Se ancla al mediodía a propósito: en los días con cambio de hora, medianoche
 * +/- un día puede caer en el día equivocado.
 */
export function desplazarDias(iso, dias) {
  const base = new Date(`${iso}T12:00:00`);
  base.setDate(base.getDate() + dias);
  return isoHoy(base);
}

/** Primer y último día del mes anterior. */
export function mesPasado(base = new Date()) {
  const inicio = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  // Día 0 del mes actual = último del anterior; resuelve febrero y los de 31.
  const fin = new Date(base.getFullYear(), base.getMonth(), 0);
  return { desde: isoHoy(inicio), hasta: isoHoy(fin) };
}

/**
 * Atajos ofrecidos. El orden es el de uso real de una contadora: lo de hoy,
 * la semana, y después los cierres de mes.
 */
export const RANGOS_RAPIDOS = [
  { clave: "hoy", etiqueta: "Hoy", resolver: () => ({ desde: isoHoy(), hasta: isoHoy() }) },
  {
    clave: "7d",
    etiqueta: "7 días",
    resolver: () => ({ desde: desplazarDias(isoHoy(), -6), hasta: isoHoy() }),
  },
  {
    clave: "30d",
    etiqueta: "30 días",
    resolver: () => ({ desde: desplazarDias(isoHoy(), -29), hasta: isoHoy() }),
  },
  {
    clave: "mes",
    etiqueta: "Este mes",
    resolver: () => ({ desde: isoInicioMes(), hasta: isoHoy() }),
  },
  { clave: "mes_pasado", etiqueta: "Mes pasado", resolver: () => mesPasado() },
  {
    clave: "ano",
    etiqueta: "Este año",
    resolver: () => ({ desde: `${new Date().getFullYear()}-01-01`, hasta: isoHoy() }),
  },
  // Sin fechas: el backend no filtra por período.
  { clave: "todo", etiqueta: "Todo", resolver: () => ({ desde: "", hasta: "" }) },
];

/**
 * Qué atajo corresponde al rango actual, o null si es uno escrito a mano.
 *
 * Sirve para que el chip quede marcado cuando el rango coincide, incluso si
 * las fechas vinieron de la URL o de una sesión anterior.
 */
export function detectarRango(desde, hasta) {
  const d = String(desde || "");
  const h = String(hasta || "");
  for (const rango of RANGOS_RAPIDOS) {
    const esperado = rango.resolver();
    if (esperado.desde === d && esperado.hasta === h) return rango.clave;
  }
  return null;
}

const MESES = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

function diaMes(iso) {
  const [a, m, d] = String(iso).split("-").map(Number);
  if (!a || !m || !d) return null;
  return { a, m, d };
}

/**
 * El rango en una línea legible: "1 – 14 oct 2026 · 14 días".
 *
 * Existe porque dos campos con 01/10/2026 y 14/10/2026 no dicen cuántos días
 * se están mirando, y ese número es justo lo que se quiere saber al comparar
 * un total contra otro.
 */
export function describirRango(desde, hasta) {
  const d = diaMes(desde);
  const h = diaMes(hasta);
  if (!d && !h) return "Todo el historial";
  if (!d) return `Hasta el ${h.d} ${MESES[h.m - 1]} ${h.a}`;
  if (!h) return `Desde el ${d.d} ${MESES[d.m - 1]} ${d.a}`;

  const dias = Math.round(
    (new Date(`${hasta}T12:00:00`) - new Date(`${desde}T12:00:00`)) / 86_400_000,
  ) + 1;
  const sufijo = dias === 1 ? "1 día" : `${dias} días`;

  if (d.a === h.a && d.m === h.m) {
    if (d.d === h.d) return `${d.d} ${MESES[d.m - 1]} ${d.a} · ${sufijo}`;
    return `${d.d} – ${h.d} ${MESES[h.m - 1]} ${h.a} · ${sufijo}`;
  }
  if (d.a === h.a) {
    return `${d.d} ${MESES[d.m - 1]} – ${h.d} ${MESES[h.m - 1]} ${h.a} · ${sufijo}`;
  }
  return `${d.d} ${MESES[d.m - 1]} ${d.a} – ${h.d} ${MESES[h.m - 1]} ${h.a} · ${sufijo}`;
}
